import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { PartialId } from '@attain-apps/string-builder'

import * as state from '../../../../../../../../state'
import * as display from '../../../../../display'

import * as entityRecordSelect from './components'

/**
 * Dialog for editing DMS records
 */
@ngCore.Component({
  imports: [
    display.value.Component,
    ds.button.Component,
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    entityRecordSelect.Component,
  ],
  selector: 'atn-edit-record-dialog',
  standalone: true,
  styleUrls: ['./edit-record-dialog.component.scss'],
  templateUrl: './edit-record-dialog.component.html',
})
export class AtnEditRecordDialogComponent {
  /** @see {@link state.dms.entities.Collection} */
  private readonly _entitiesEc = ngCore.inject(state.dms.entities.Collection)
  /** @see {@link state.dms.snapshots.Collection} */
  private readonly _snapshotsEc = ngCore.inject(state.dms.snapshots.Collection)

  /** @see {@link state.dms.records.Collection,} */
  public readonly recordsEc = ngCore.inject(state.dms.records.Collection)

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /**
   * Displays values {@link ents.core.dms.data.Record.Values} for the selected record
   */
  public readonly recordValuesSg = ngCore.computed(
    (
      record = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal(),
    ) => record?.values ?? {},
  )

  /**
   * Displays parentId {@link ents.core.dms.data.Record.parentId} for the selected record
   */
  public readonly recordParentIdSg = ngCore.computed(
    (
      record = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal(),
    ) => record?.parentId,
  )

  /**
   * List of entity attributes
   */
  // TODO: Maybe refactor to map options and other staff to attribute so that we
  // don't have a lot of lookups
  private readonly _attributesSg = ngCore.computed(
    (selectedModel = this._currentModelSg()) =>
      selectedModel?.attributes?.map(attribute => {
        const id = ents.core.dms.models.Id.Parts.from(attribute).partialId
        return {
          ...attribute,
          id,
        }
      }),
  )

  /**
   * List of attributes displayed in the edit dialog.
   * During creation, ID is hidden; during update, ID is shown as readonly.
   */
  public readonly displayAttributesSg = ngCore.computed(
    (
      attributes = this._attributesSg(),
      isUpdating = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .isUpdating()
        .asSignal(),
      context = this.recordsEc.feature.selectors.context.asSignal(),
      record = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal(),
    ) => {
      const recordId = record?.values?.[ents.core.dms.data.Record.Id.KEY]
      return attributes
        ?.filter(
          attribute =>
            !attribute.computation &&
            (isUpdating || attribute.id !== ents.core.dms.data.Record.Id.KEY),
        )
        .map(attribute => ({
          ...attribute,
          readonly: attribute.id === ents.core.dms.data.Record.Id.KEY,
          documentKey: context?.modelId
            ? state.dms.records.documentKeyFor({
                modelId: context.modelId,
                recordId: ents.core.dms.data.Record.Id.isId(recordId)
                  ? recordId
                  : undefined,
                attributeId: attribute.id,
              })
            : undefined,
        }))
    },
  )

  /**
   * Audience used to download/delete already-persisted Files-typed values,
   * and to upload newly-picked ones once the record is saved. `undefined`
   * while the record hasn't been saved yet (create mode).
   */
  public readonly recordTargetSg = ngCore.computed(
    (
      context = this.recordsEc.feature.selectors.context.asSignal(),
      record = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal(),
    ): ents.core.agent.KnowledgeBase.Target.Record | undefined => {
      const recordId = record?.values?.[ents.core.dms.data.Record.Id.KEY]
      if (!context?.modelId || !ents.core.dms.data.Record.Id.isId(recordId)) {
        return undefined
      }
      return {
        tenantId: context.tenantId,
        zoneId: context.zoneId,
        modelId: context.modelId,
        recordId,
        type: ents.core.agent.KnowledgeBase.Target.Type.Record,
      }
    },
  )

  /** Current entity's parent */
  public readonly entityParentSg = ngCore.computed(
    (selectedModel = this._currentModelSg()) =>
      ents.core.dms.models.Entity.shouldBeEntity(selectedModel)
        ? this._schemaFeature.selectors.schema
            .asSignal()
            ?.tryGetParentEntityOf(selectedModel)
        : undefined,
  )

  /**
   * Selected {@link state.dms.entities.Entity}
   */
  private readonly _currentModelSg = ngCore.computed(
    () =>
      this._entitiesEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal() ??
      this._snapshotsEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal(),
  )

  /**
   * Shows dialog title based on the current model.
   */
  public readonly titleSg = ngCore.computed(
    (
      modelName = this._currentModelSg()?.name,
      isUpdating = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .isUpdating()
        .asSignal(),
    ) => `${isUpdating ? 'Edit' : 'Add'} ${modelName}`,
  )

  /**
   * Handles the change of the parent record ID.
   */
  public recordParentIdChangeHandler(value: unknown): void {
    if (ents.core.dms.data.Record.Id.isId(value)) {
      this.recordsEc.plugins.editingPlugin.feature.actions.edit
        .from(`Dialog.Form.ParentRecord.Changed`)
        .dispatch({
          edit: record => {
            if (!record) {
              return
            }
            record.parentId = value
          },
        })
    }
  }

  /**
   * Handles the change of an attribute value.
   */
  public attributeValueChangeHandler(
    attributeId: PartialId,
    value: ents.core.dms.data.Value,
  ): void {
    this.recordsEc.plugins.editingPlugin.feature.actions.edit
      .from(`Dialog.Form.Input-${attributeId}.Changed`)
      .dispatch({
        edit: record => {
          if (!record) {
            return
          }
          record.values ??= {}
          record.values[attributeId] = value
        },
      })
  }

  /**
   * Handling save click
   */
  public saveClickHandler() {
    this.recordsEc.plugins.editingPlugin.feature.actions.submit
      .from('Dialog.Edit.Footer.Buttons.Save.Clicked')
      .dispatch({
        current: true,
      })
  }

  /**
   * Handling cancel click
   */
  public cancelBtnClickHandler() {
    this.recordsEc.plugins.editingPlugin.feature.actions.discard
      .from(' Dialog.Edit.Footer.Buttons.Cancel.Clicked')
      .dispatch({
        current: true,
      })
  }
}
